import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { DialogAction, DialogBaseExtendProps } from 'components/_new/Dialog';
import { useKnowledgeBase } from 'components/knowledgeBase/knowledgeBaseContext';
import { useGlobal } from 'contexts/globalContext';
import { MessageBox } from 'components/_new/MessageBox';
import { Loader } from 'components/_new/Loader';
import { useScope } from 'contexts/scopeContext';
import { LanguageFormData, ModifyDirectoryForm } from './ModifyDirectoryForm';

type ModifyDirectoryDialogProps = DialogBaseExtendProps & { directoryId?: number | null };
export const ModifyDirectoryDialog = ({ directoryId, ...dialogProps }: ModifyDirectoryDialogProps) => {
	const { t } = useTranslation();

	const { toastRef } = useGlobal();
	const { currentProgram: data } = useScope();
	const { directoriesQuery } = useKnowledgeBase();

	const {
		data: knowledgeBaseDirectory,
		isSuccess: knowledgeBaseDirectorySuccess,
		isLoading: knowledgeBaseDirectoryLoading,
		remove: knowledgeBaseDirectoryRemove,
	} = useQuery(
		['KNOWLEDGE_BASE_PP_DIRECTORY', { directoryId, programId: Number(data?.id) }],
		() => knowledgeBaseApi.program.getProgramDirectory(Number(data?.id), Number(directoryId)),
		{
			enabled: Boolean(dialogProps.visible) && Boolean(directoryId),
		}
	);
	const knowledgeBaseDirectoryLoadingFinal = directoryId ? knowledgeBaseDirectoryLoading : false;

	const currentTranslations = knowledgeBaseDirectorySuccess
		? knowledgeBaseDirectory.translations!.map(({ languageCode, text, slug }) => ({
				language: languageCode,
				name: text,
				slug,
		  }))
		: undefined;

	const {
		data: knowledgeBaseLanguages,
		isLoading: knowledgeBaseLanguagesLoading,
		isSuccess: knowledgeBaseLanguagesSuccess,
	} = useQuery(
		['KNOWLEDGE_BASE_PP_DIRECTORY_LNGS', { programId: Number(data?.id) }],
		() => knowledgeBaseApi.program.getProgramAvailableLangauges(Number(data?.id)),
		{
			enabled: Boolean(dialogProps.visible),
		}
	);

	const { mutate: addDirectoryMutate } = useMutation(
		(translations: LanguageFormData[]) =>
			knowledgeBaseApi.program.addProgramDirectory(Number(data?.id), translations),
		{
			onSuccess: () => {
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('knowledgeBase.programPanel.directoryAdded'),
				});
				knowledgeBaseDirectoryRemove();
				directoriesQuery.refetch();
				dialogProps?.onHide?.();
			},
		}
	);

	const { mutate: editDirectoryMutate } = useMutation(
		(translations: LanguageFormData[]) =>
			knowledgeBaseApi.program.editProgramDirectory(Number(data?.id), Number(directoryId), translations),
		{
			onSuccess: () => {
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('knowledgeBase.programPanel.directoryUpdated'),
				});
				knowledgeBaseDirectoryRemove();
				directoriesQuery.refetch();
				dialogProps?.onHide?.();
			},
		}
	);

	const handleSubmit = (values: LanguageFormData[]) => {
		if (directoryId) {
			editDirectoryMutate(values);
		} else {
			addDirectoryMutate(values);
		}
	};

	return (
		<DialogAction
			title={
				directoryId
					? t('knowledgeBase.programPanel.editDirectory')
					: t('knowledgeBase.programPanel.addNewDirectory')
			}
			actions={[
				{
					key: 'save',
					form: 'edit-directory-form',
					label: directoryId ? t('actions.save') : t('actions.add'),
					submit: true,
				},
			]}
			{...dialogProps}
		>
			{knowledgeBaseLanguagesLoading && knowledgeBaseDirectoryLoadingFinal && <Loader />}
			{!knowledgeBaseLanguagesLoading && knowledgeBaseLanguagesSuccess && !knowledgeBaseDirectoryLoadingFinal && (
				<div className="flex flex-column gap-2">
					{!directoryId && (
						<MessageBox message={t('knowledgeBase.programPanel.addNewDirectoryDesc')} variant="rose" />
					)}
					<ModifyDirectoryForm
						programLanguage={knowledgeBaseLanguages.program}
						optionalLanguages={knowledgeBaseLanguages.optional}
						currentTranslations={currentTranslations}
						onSubmit={handleSubmit}
					/>
				</div>
			)}
		</DialogAction>
	);
};
